import { create } from 'zustand';
import dayjs from 'dayjs';
import { fetchShiftsLocationFilters, fetchShiftsQualificationFilters } from '@libs/api/shifts';
import { fetchShiftsList } from '@libs/api/shifts/fetchShiftsList';
import { Qualification } from '@libs/models/talents';
import { ShiftFilter } from '@libs/models/shifts/shift';
import { Shift } from '@libs/models/shifts';
import { DEFAULT_PAGE_SIZE } from '@molecules/dataDisplay';

type State = {
  loading: boolean;
  initiated: boolean;
  data: Shift[];
  page: number;
  total: number;
  perPage: number;
  load: (page: number, perPage: number, filter?: ShiftFilter) => Promise<void>;
  reload: () => Promise<void>;
  lastFilter?: ShiftFilter;
  showDetails: boolean;
  currentShift: Shift | null;
  openShiftDetails: (id: string) => void;
  closeShiftDetails: () => void;
  loadFilters: () => Promise<void>;
  filters?: {
    locations: string[];
    qualifications: Qualification[];
  };
  setPagination: (page: number, perPage: number) => void;
};

export const useShiftsListStore = create<State>()((set, get) => ({
  loading: false,
  initiated: false,
  data: [],
  page: 1,
  perPage: DEFAULT_PAGE_SIZE,
  total: 0,
  load: async (page, perPage, filter) => {
    const { currentShift } = get();
    set({ loading: true, lastFilter: filter });
    try {
      const response = await fetchShiftsList(page, perPage, {
        ...filter,
        // When date filter is `All`, we should start from the beginning of current day
        startDate: filter?.startDate ?? dayjs().startOf('day').toServerDateTimeFormat(),
        endDate: filter?.endDate ?? undefined,
        timeZone: filter?.timeZone ?? undefined,
      });
      set({
        loading: false,
        data: response.items,
        page: response.pageNumber,
        perPage: response.pageSize,
        total: response.totalCount,
      });
      if (currentShift) {
        set({ currentShift: response.items.find((s) => s.id === currentShift.id) });
      }
    } finally {
      set({ loading: false, initiated: true });
    }
  },
  reload: async () => {
    const { load, page, perPage, lastFilter } = get();
    return load(page, perPage, lastFilter);
  },
  currentShift: null,
  showDetails: false,
  openShiftDetails: (id) => {
    const { data } = get();
    const shift = data.find((s) => s.id === id);
    set({ currentShift: shift, showDetails: true });
  },
  closeShiftDetails: () => {
    set({ showDetails: false });
  },
  loadFilters: async () => {
    const [locationsRes, qualificationRes] = await Promise.all([
      fetchShiftsLocationFilters(),
      fetchShiftsQualificationFilters(),
    ]);

    set({ filters: { locations: locationsRes.data, qualifications: qualificationRes.data } });
  },
  setPagination: (page, perPage) => {
    set({ page, perPage });
  },
}));
